import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

const navItems = [
  { to: "/", label: "Startseite", testId: "nav-home" },
  { to: "/fahrzeuge", label: "Fahrzeuge", testId: "nav-inventory" },
  { to: "/ueber-uns", label: "Über uns", testId: "nav-about" },
  { to: "/kontakt", label: "Kontakt", testId: "nav-contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col">
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 h-32 md:h-40 flex items-center justify-between">
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <img
              src="/logo.png"
              alt="D.R Automobile — Qualität, die bewegt"
              className="h-28 md:h-36 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-crisp ${
                    isActive ? "text-white border-b-2 border-amber-400 pb-1" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+436677608726"
              data-testid="header-phone"
              className="flex items-center gap-2 text-sm font-medium text-white hover:text-amber-400 transition-crisp"
            >
              <Phone size={16} className="text-amber-400" />
              +43 667 760 8726
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white"
            aria-label="Menü"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-800 bg-slate-900" data-testid="mobile-menu">
            <div className="px-4 py-4 flex flex-col gap-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-${n.testId}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-sm font-medium ${isActive ? "bg-amber-400 text-slate-900" : "text-slate-200 hover:bg-slate-800"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <a href="tel:+436677608726" className="px-3 py-3 text-sm font-medium text-amber-400 flex items-center gap-2">
                <Phone size={16} /> +43 667 760 8726
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-slate-900 text-slate-300 mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="mb-4">
              <img
                src="/logo.png"
                alt="D.R Automobile"
                className="h-32 w-auto object-contain"
              />
            </div>
            <p className="text-sm leading-relaxed text-slate-400 max-w-md">
              Ihr verlässlicher Partner für geprüfte Gebrauchtwagen in Österreich. Persönliche Beratung,
              transparente Abwicklung — vom Inhaber für Sie.
            </p>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-white mb-4 uppercase text-xs tracking-widest">Kontakt</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 flex-shrink-0" />
                <span>Ringgasse 6<br />4840 Vöcklabruck, Österreich</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={14} />
                <a href="tel:+436677608726" className="hover:text-white transition-crisp">+43 667 760 8726</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} />
                <a href="mailto:kontakt@dr-automobile.at" className="hover:text-white transition-crisp">kontakt@dr-automobile.at</a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-white mb-4 uppercase text-xs tracking-widest">Öffnungszeiten</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex justify-between"><span>Mo–Fr</span><span className="text-slate-200">09:00 – 18:00</span></li>
              <li className="flex justify-between"><span>Sa</span><span className="text-slate-200">09:00 – 13:00</span></li>
              <li className="flex justify-between"><span>So</span><span className="text-slate-500">Geschlossen</span></li>
            </ul>
            <p className="text-xs text-slate-500 mt-4">Besichtigungen auch nach Terminvereinbarung.</p>
          </div>
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">© {new Date().getFullYear()} D.R Automobile. Alle Rechte vorbehalten.</p>
            <div className="flex gap-6 text-xs text-slate-400">
              <Link to="/impressum" data-testid="footer-impressum" className="hover:text-white transition-crisp">Impressum</Link>
              <Link to="/datenschutz" data-testid="footer-privacy" className="hover:text-white transition-crisp">Datenschutz</Link>
              <Link to="/agb" data-testid="footer-agb" className="hover:text-white transition-crisp">AGB</Link>
              <Link to="/admin/login" data-testid="footer-admin" className="hover:text-white transition-crisp">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
